/**
 * @spec docs/specs/features/staking-ui-batched-reads.md (R9)
 *
 * The one way to refresh balances after something changed them. Every transaction flow calls
 * `invalidateBalances` (or `refreshBalancesAfterTx`, which finds the receipt block first); no
 * code invalidates a balance query key directly.
 */
import type { QueryClient } from '@tanstack/react-query';
import { queryClient as sharedQueryClient } from './query-client';
import { getBaseProvider } from './provider';
import { raiseFloor, requestBypass, type FloorScope } from './balance-floors';
import type { ReadChain } from './read-api';

export const BALANCES_QUERY_ROOT = 'balances';
export const CHIP_REDEMPTIONS_QUERY_ROOT = 'chip-redemptions';

export interface InvalidateBalancesOptions {
  /** Receipt block per chain: balances below it are not accepted as current. */
  readonly minBlock?: Partial<Record<ReadChain, bigint | number>>;
  /**
   * No block is known (e.g. the Polygon legacy claim, a redemption refund): ask for an uncached
   * read of the given reads (each balances chain and/or the CHIP-redemptions total), or of all
   * three for `true`. Until it delivers, those values are not fresh.
   */
  readonly bypassCache?: true | readonly FloorScope[];
  /** Addresses the change touched; defaults to every address with a balances query. */
  readonly addresses?: readonly string[];
}

/** Every address with a balances or CHIP-redemptions query. */
function cachedAddresses(client: QueryClient): string[] {
  const out = new Set<string>();
  for (const root of [BALANCES_QUERY_ROOT, CHIP_REDEMPTIONS_QUERY_ROOT]) {
    for (const query of client.getQueryCache().findAll({ queryKey: [root] })) {
      const key = query.queryKey[1];
      for (const address of Array.isArray(key) ? key : [key]) {
        if (typeof address === 'string') out.add(address);
      }
    }
  }
  return [...out];
}

export async function invalidateBalances(
  options: InvalidateBalancesOptions = {},
  client: QueryClient = sharedQueryClient
): Promise<void> {
  const addresses = options.addresses ?? cachedAddresses(client);
  const now = Date.now();
  for (const chain of ['base', 'polygon'] as const) {
    const block = options.minBlock?.[chain];
    if (block === undefined) continue;
    for (const address of addresses) {
      raiseFloor(chain, address, BigInt(block), now);
      if (chain === 'base') raiseFloor('redemptions', address, BigInt(block), now);
    }
  }
  if (options.bypassCache) {
    const scopes: readonly FloorScope[] =
      options.bypassCache === true ? ['base', 'polygon', 'redemptions'] : options.bypassCache;
    for (const scope of scopes) requestBypass(scope, addresses, now);
  }
  await Promise.all([
    client.invalidateQueries({ queryKey: [BALANCES_QUERY_ROOT] }),
    client.invalidateQueries({ queryKey: [CHIP_REDEMPTIONS_QUERY_ROOT] }),
  ]);
}

export interface ConfirmedTx {
  readonly chain: ReadChain;
  readonly txHash?: string | null;
  /** The receipt block when the flow already has the receipt. */
  readonly blockNumber?: number | bigint | null;
}

/**
 * What a blockless confirmation on `chain` forces. A Base transaction of unknown kind may be a
 * redemption payout or refund, so it also refreshes the CHIP-redemptions total.
 */
function blocklessScopes(chain: ReadChain): readonly FloorScope[] {
  return chain === 'base' ? ['base', 'redemptions'] : ['polygon'];
}

/** Receipt block of a Base transaction, read through the relay; null when unknown. */
async function baseReceiptBlock(txHash: string): Promise<number | null> {
  try {
    const receipt = await getBaseProvider().getTransactionReceipt(txHash);
    return receipt?.blockNumber ?? null;
  } catch {
    return null;
  }
}

/**
 * Refresh balances after a confirmed transaction. Gasless flows only know the transaction hash,
 * so the receipt block is read through the relay; with no block the refresh bypasses the cache.
 */
export async function refreshBalancesAfterTx(
  tx: ConfirmedTx,
  client: QueryClient = sharedQueryClient
): Promise<void> {
  let block = tx.blockNumber ?? null;
  if (block === null && tx.chain === 'base' && tx.txHash) {
    block = await baseReceiptBlock(tx.txHash);
  }
  await invalidateBalances(
    block === null
      ? { bypassCache: blocklessScopes(tx.chain) }
      : { minBlock: { [tx.chain]: block } },
    client
  );
}
